import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import BatchDetailPanel from './BatchDetailPanel';
import DprDayDetail from './DprDayDetail';
import BatchWhatsappButton from './BatchWhatsappButton';
import SyncFromCrmButton from './SyncFromCrmButton';
import { toast } from 'sonner';
import { BarChart3, CheckCircle2, AlertTriangle, Clock, FileSpreadsheet, FileText, Download, Loader2, TrendingUp, Award, ClipboardList, ShieldCheck, BookOpen, ChevronDown, ChevronRight, CalendarOff } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PeriodSelector, fmtDate } from './periodUtils';

const A = (r, k) => (r.answers || {})[k] || '';
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };

// Compact columns for the in-report tables (full detail lives in the row modal).
const TABLE_COLUMNS = [
  { key: 'date', label: 'Date', get: (r) => fmtDate(r.date) },
  { key: 'trainer_name', label: 'Trainer', get: (r) => r.trainer_name },
  { key: 'batch_number', label: 'Batch No', get: (r) => String(r.batch_number) },
  { key: 'batch_name', label: 'Batch Name', get: (r) => r.batch_name },
  { key: 'dpr_status', label: 'DPR Status', get: (r) => ({ filled: 'DPR Filled', not_conducted: 'Not Conducted', missing: 'DPR Missing' }[r.dpr_status] || '') },
  { key: 'audit_status', label: 'Audit Status', get: (r) => r.audit_status },
  { key: 'issue_type', label: 'Issue Type(s)', get: (r) => { const i = r.issue || {}; const t = i.types || (i.type ? [i.type] : []); return t.join(', '); } },
  { key: 'issue_severity', label: 'Severity', get: (r) => (r.issue || {}).severity || '' },
  { key: 'audited_at', label: 'Audit Time', get: (r) => r.audited_at ? new Date(r.audited_at).toLocaleString('en-IN') : '' },
  { key: 'manager_name', label: 'Training Manager', get: (r) => r.manager_name },
];

const COLUMNS = [
  { key: 'date', label: 'Date', get: (r) => fmtDate(r.date) },
  { key: 'week_start', label: 'Week of', get: (r) => fmtDate(r.week_start) },
  { key: 'trainer_name', label: 'Trainer', get: (r) => r.trainer_name },
  { key: 'batch_number', label: 'Batch No', get: (r) => String(r.batch_number) },
  { key: 'batch_name', label: 'Batch Name', get: (r) => r.batch_name },
  { key: 'timing', label: 'Timing', get: (r) => r.timing },
  { key: 'dpr_conducted', label: 'DPR Conducted', get: (r) => r.dpr_conducted },
  { key: 'dpr_status', label: 'DPR Status', get: (r) => ({ filled: 'DPR Filled', not_conducted: 'Not Conducted', missing: 'DPR Missing' }[r.dpr_status] || '') },
  { key: 'strength', label: 'Strength', get: (r) => String(r.strength) },
  { key: 'present', label: 'Present', get: (r) => String(r.present) },
  { key: 'absent', label: 'Absent', get: (r) => String(r.absent) },
  { key: 'mode', label: 'Mode', get: (r) => r.mode },
  { key: 'manager_name', label: 'Training Manager', get: (r) => r.manager_name },
  { key: 'audit_status', label: 'Audit Status', get: (r) => r.audit_status },
  { key: 'issue_type', label: 'Issue Type(s)', get: (r) => { const i = r.issue || {}; const t = i.types || (i.type ? [i.type] : []); return t.join(', '); } },
  { key: 'issue_severity', label: 'Severity', get: (r) => (r.issue || {}).severity || '' },
  { key: 'audited_at', label: 'Audit Date/Time', get: (r) => r.audited_at ? new Date(r.audited_at).toLocaleString('en-IN') : '' },
  { key: 'manager_remarks', label: 'Manager Remarks', get: (r) => r.manager_remarks || '' },
];

const FINAL_QA = [
  ['Syllabus completed', 'syllabus_completed'], ['All PDP done', 'all_pdp'], ['All Mock Interviews', 'all_mock'],
  ['All Assessments', 'all_assessment'], ['Final Project', 'final_project'], ['Overall attendance OK', 'overall_attendance'],
  ['Placement ready', 'placement_ready'], ['All student feedback completed', 'student_feedback'], ['Google reviews collected', 'google_reviews'], ['Unresolved issues', 'unresolved_issues'],
];

function FinalDetailModal({ row, onClose }) {
  if (!row) return null;
  const ans = row.answers || {};
  const iss = row.issue || {};
  const types = iss.types || (iss.type ? [iss.type] : []);
  const answered = !!row.audit_id;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="final-detail-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-violet-600" />Final Audit — Batch {row.batch_number} · {row.trainer_name}</DialogTitle>
            <BatchWhatsappButton link={row.whatsapp_link} source={row.whatsapp_source} className="shrink-0" />
          </div>
          <p className="text-[11px] text-slate-500">{row.batch_name} · Ended {row.end_date || '—'} · Manager: {row.manager_name} · <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${dot(row.status_color)}`} />{row.audit_status}</span></p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
          {answered ? (
            <div>
              <div className="flex items-center gap-2 mb-2"><span className="text-[12px] text-slate-500">Overall rating:</span><Badge className="bg-violet-100 text-violet-700 border-0">{row.overall_rating || '—'}</Badge></div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {FINAL_QA.map(([label, key]) => (
                  <div key={key} className="flex items-center justify-between text-[12px] border-b border-slate-50 py-1"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-700">{ans[key] || '—'}</span></div>
                ))}
              </div>
              {types.length > 0 && <div className="mt-2 p-2 rounded bg-rose-50 border border-rose-100 text-[12px]"><p className="text-rose-700 font-medium">Issue ({iss.severity}): {types.join(', ')}</p><p className="text-slate-600 mt-0.5">{iss.description}</p></div>}
              {row.final_remarks && <p className="mt-2 text-[12px]"><span className="text-slate-400">Remarks: </span>{row.final_remarks}</p>}
              <p className="mt-1 text-[11px] text-slate-400">Audited by {row.audited_by_name} · {row.audited_at ? new Date(row.audited_at).toLocaleString('en-IN') : ''}</p>
            </div>
          ) : <p className="text-[12px] text-rose-600 font-medium">Final audit not completed yet for this batch.</p>}
          <div><h4 className="text-[11px] font-bold uppercase tracking-wider text-violet-600 mb-1">Syllabus & attendance</h4><BatchDetailPanel batchId={row.batch_id} /></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManagerBatchesModal({ mgr, rows, onClose, onRowClick }) {
  if (!mgr) return null;
  const mrows = rows.filter((r) => (r.manager_user_id || '') === (mgr.manager_user_id || ''));
  return (
    <Dialog open={!!mgr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="mgr-batches-modal">
        <DialogHeader>
          <DialogTitle className="text-base">{mgr.manager_name} — assigned batches</DialogTitle>
          <p className="text-[11px] text-slate-500">{mgr.assigned_batches} sessions · {mgr.audits_done} done · {mgr.pending} pending · {mgr.issues} issues · {mgr.completion_pct}% completion — click a row for full detail</p>
        </DialogHeader>
        <div className="flex-1 overflow-auto border border-slate-200 rounded-lg">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-500"><th className="py-1.5 px-2">Date</th><th className="py-1.5 px-2">Batch</th><th className="py-1.5 px-2">Trainer</th><th className="py-1.5 px-2">DPR</th><th className="py-1.5 px-2">Status</th></tr></thead>
            <tbody>
              {mrows.map((r, i) => (
                <tr key={`${r.batch_id}-${r.date}-${i}`} onClick={() => onRowClick(r)} className="border-b border-slate-50 hover:bg-violet-50 cursor-pointer" data-testid={`mgr-batch-row-${r.batch_id}-${r.date}`}>
                  <td className="py-1.5 px-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="py-1.5 px-2"><span className="font-medium text-slate-700">{r.batch_number}</span> <span className="text-slate-400">{r.batch_name}</span></td>
                  <td className="py-1.5 px-2 text-slate-600">{r.trainer_name}</td>
                  <td className="py-1.5 px-2">{r.dpr_conducted}</td>
                  <td className="py-1.5 px-2"><span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${dot(r.status_color)}`} />{r.audit_status}</span></td>
                </tr>
              ))}
              {mrows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No batches for this manager in the period.</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const KpiCard = ({ label, value, sub, color, testid, onClick, active }) => (
  <Card className={`p-4 transition-all ${onClick ? 'cursor-pointer hover:border-violet-300 hover:shadow-md' : ''} ${active ? 'border-violet-400 ring-1 ring-violet-200' : ''}`} data-testid={testid} onClick={onClick}>
    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
    <p className={`text-3xl font-bold mt-1 ${color || 'text-slate-800'}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
  </Card>
);

const dot = (c) => ({ green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-rose-500', grey: 'bg-slate-300' }[c] || 'bg-slate-300');

const DPR_BADGE = {
  filled: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'DPR Filled' },
  not_conducted: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Not Conducted' },
  missing: { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'DPR Missing' },
};
const DprBadge = ({ status }) => { const m = DPR_BADGE[status] || DPR_BADGE.filled; return <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${m.cls}`}>{m.label}</span>; };

function FilterTable({ rows, testid, onRowClick }) {
  const [filters, setFilters] = useState({});
  const filtered = useMemo(() => rows.filter((r) => TABLE_COLUMNS.every((c) => {
    const f = (filters[c.key] || '').trim().toLowerCase();
    if (!f) return true;
    return String(c.get(r) || '').toLowerCase().includes(f);
  })), [rows, filters]);
  return (
    <div className="border border-slate-200 rounded-lg overflow-auto max-h-[65vh]" data-testid={testid}>
      <table className="text-[11px] border-collapse w-full">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-100">
            {TABLE_COLUMNS.map((c) => <th key={c.key} className="px-2 py-1.5 text-left font-semibold text-slate-600 whitespace-nowrap border-b border-slate-200">{c.label}</th>)}
          </tr>
          <tr className="bg-white">
            {TABLE_COLUMNS.map((c) => (
              <th key={c.key} className="px-1 py-1 border-b border-slate-200">
                <input value={filters[c.key] || ''} onChange={(e) => setFilters((p) => ({ ...p, [c.key]: e.target.value }))}
                  placeholder="filter" className="w-full min-w-[60px] px-1.5 py-0.5 text-[10px] border border-slate-200 rounded font-normal" data-testid={`col-filter-${c.key}`} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <tr key={`${r.batch_id}-${r.date}-${i}`} onClick={() => onRowClick?.(r)} className="hover:bg-violet-50 cursor-pointer" data-testid={`row-${r.batch_id}-${r.date}`}>
              {TABLE_COLUMNS.map((c) => (
                <td key={c.key} className={`px-2 py-1 border-b border-slate-50 ${c.key === 'batch_name' || c.key === 'issue_type' ? 'max-w-[180px] truncate' : 'whitespace-nowrap'} ${c.key === 'audit_status' ? '' : 'text-slate-600'}`}
                    title={c.key === 'batch_name' || c.key === 'issue_type' ? (c.get(r) || '') : undefined}>
                  {c.key === 'audit_status'
                    ? <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${dot(r.status_color)}`} />{r.audit_status}</span>
                    : c.key === 'dpr_status'
                      ? <DprBadge status={r.dpr_status} />
                      : c.key === 'issue_severity' && (c.get(r))
                        ? <span className="text-amber-700 font-medium">{c.get(r)}</span>
                        : (c.get(r) || '—')}
                </td>
              ))}
            </tr>
          ))}
          {filtered.length === 0 && <tr><td colSpan={TABLE_COLUMNS.length} className="text-center py-8 text-slate-400">No rows match the filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const QA = [
  ['Batch actually taken', 'batch_taken'], ['Trainer present', 'trainer_present'], ['Duration completed', 'duration_completed'],
  ['Mode verified', 'mode_verified'], ['Attendance taken', 'attendance_taken'], ['Attendance accurate', 'attendance_accurate'],
  ['Attendance updated', 'attendance_updated'], ['PDP', 'pdp'], ['Mock Interview', 'mock_interview'],
  ['Assessment', 'assessment'], ['Project', 'project'], ['Planned activity', 'planned_activity'], ['Issue found', 'issue_found'],
];

function RowDetailModal({ row, onClose }) {
  const [showContext, setShowContext] = useState(false);
  if (!row) return null;
  const iss = row.issue || {};
  const types = iss.types || (iss.type ? [iss.type] : []);
  const answered = !!row.audit_id;
  const hasIssue = types.length > 0;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0 rounded-2xl" data-testid="row-detail-modal">
        <DialogHeader className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-base flex items-center gap-2">Batch {row.batch_number} · {row.trainer_name}</DialogTitle>
            <BatchWhatsappButton link={row.whatsapp_link} source={row.whatsapp_source} className="shrink-0" />
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-slate-500">
            <span>{fmtDate(row.date)}</span><span>·</span><span>{row.batch_name}</span><span>·</span>
            <span>Manager: {row.manager_name}</span>
            <span className="inline-flex items-center gap-1.5 ml-auto font-semibold text-slate-700"><span className={`w-2 h-2 rounded-full ${dot(row.status_color)}`} />{row.audit_status}</span>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Two panels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Trainer's DPR */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3.5" data-testid="panel-dpr">
              <div className="flex items-center justify-between mb-2.5">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" />Trainer's DPR</h4>
                <DprBadge status={row.dpr_status} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-2">
                {[['Timing', row.timing || '—'], ['Mode', row.mode || '—'], ['Strength', row.strength]].map(([k, v]) => (
                  <div key={k} className="bg-white rounded-lg py-1.5 border border-slate-100"><p className="text-[9px] uppercase tracking-wide text-slate-400">{k}</p><p className="text-[12px] font-semibold text-slate-700 truncate px-1">{v}</p></div>
                ))}
                {[['Present', row.present], ['Absent', row.absent], ['Conducted', row.dpr_conducted]].map(([k, v]) => (
                  <div key={k} className="bg-white rounded-lg py-1.5 border border-slate-100"><p className="text-[9px] uppercase tracking-wide text-slate-400">{k}</p><p className="text-[12px] font-semibold text-slate-700 truncate px-1">{v}</p></div>
                ))}
              </div>
              {row.dpr_status === 'missing' && <p className="text-[11px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-2 py-1">Trainer did not submit a DPR for this working day.</p>}
              {row.dpr_reason && <p className="text-[11px] text-amber-700"><span className="font-medium">Reason not conducted:</span> {row.dpr_reason}</p>}
              {row.dpr_comments && <p className="text-[11px] text-slate-500 mt-1"><span className="font-medium text-slate-600">Trainer comments:</span> {row.dpr_comments}</p>}
            </div>

            {/* Manager's Audit */}
            <div className={`rounded-xl border p-3.5 ${answered ? (hasIssue ? 'border-amber-200 bg-amber-50/40' : 'border-emerald-100 bg-emerald-50/40') : (row.auto_cleared ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-200 bg-slate-50')}`} data-testid="panel-audit">
              <div className="flex items-center justify-between mb-2.5">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-violet-700 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Manager's Audit</h4>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700"><span className={`w-2 h-2 rounded-full ${dot(row.status_color)}`} />{answered ? (hasIssue ? 'Issue' : 'Satisfactory') : (row.auto_cleared ? 'Auto-cleared' : 'Pending')}</span>
              </div>
              {answered ? (
                <div className="space-y-2">
                  {hasIssue ? (
                    <div>
                      <div className="flex flex-wrap gap-1.5">
                        {types.map((t) => <span key={t} className="inline-flex items-center rounded-md bg-rose-100 text-rose-700 px-1.5 py-0.5 text-[10px] font-semibold">{t}</span>)}
                        {iss.severity && <span className="inline-flex items-center rounded-md bg-rose-600 text-white px-1.5 py-0.5 text-[10px] font-semibold">{iss.severity}</span>}
                      </div>
                      {iss.description && <p className="text-[12px] text-slate-700 mt-1.5">{iss.description}</p>}
                    </div>
                  ) : <p className="text-[12px] text-emerald-700 font-medium">No issues reported — satisfactory.</p>}
                  {row.manager_remarks && <p className="text-[12px]"><span className="text-slate-400">Remarks: </span>{row.manager_remarks}</p>}
                  <p className="text-[11px] text-slate-400 pt-1 border-t border-slate-200/60">Audited by {row.audited_by_name || '—'} · {row.audited_at ? new Date(row.audited_at).toLocaleString('en-IN') : ''}</p>
                </div>
              ) : row.auto_cleared ? (
                <div className="text-[12px] text-slate-600 py-1 space-y-1">
                  <p className="font-medium text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Auto-cleared — no class held, so no issues by default.</p>
                  {row.dpr_reason && <p>Reason given: <span className="font-medium text-slate-700">{row.dpr_reason}</span></p>}
                  <p className="text-[11px] text-slate-400">Counts as a satisfactory audit. The manager can still audit this day to override.</p>
                  <p className="text-[11px] text-slate-500 pt-1 border-t border-slate-200/60 flex items-center gap-1.5">
                    <span className="inline-flex items-center rounded bg-slate-200 text-slate-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">System-set</span>
                    Set by <span className="font-medium text-slate-700">{row.audited_by_name || 'System (auto-clear)'}</span>{row.audited_at ? ` · ${fmtDate(row.audited_at)}` : ''}
                  </p>
                </div>
              ) : (
                <div className="text-[12px] text-slate-500 py-2">
                  <p className="font-medium text-amber-700">Awaiting audit by the training manager.</p>
                </div>
              )}
            </div>
          </div>

          {/* DPR for this day (student attendance) */}
          <DprDayDetail batchId={row.batch_id} date={row.date} />

          {/* Collapsible batch context */}
          <div className="rounded-xl border border-slate-200">
            <button type="button" onClick={() => setShowContext((v) => !v)} className="w-full flex items-center justify-between px-3.5 py-2.5 text-[12px] font-semibold text-slate-600" data-testid="context-toggle">
              <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-violet-600" />Batch context — syllabus completion & student attendance</span>
              {showContext ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showContext && <div className="px-3.5 pb-3.5"><BatchDetailPanel batchId={row.batch_id} /></div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditReportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [range, setRange] = useState(null);
  const [trainerId, setTrainerId] = useState('all');
  const [managerId, setManagerId] = useState('all');
  const [issueType, setIssueType] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [batchType, setBatchType] = useState('all');
  const [tab, setTab] = useState('completed');
  const [trainerModal, setTrainerModal] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [finalData, setFinalData] = useState(null);
  const [finalDetail, setFinalDetail] = useState(null);
  const [mgrModal, setMgrModal] = useState(null);
  const [holiday, setHoliday] = useState(null);

  useEffect(() => {
    setHoliday(null);
    if (range && range.from_date && range.from_date === range.to_date) {
      api.get('/training/holidays/check', { params: { date: range.from_date } })
        .then((r) => { if (r.data?.is_holiday) setHoliday(r.data.name); })
        .catch(() => {});
    }
  }, [range]);

  const load = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const params = { mode: 'custom', from_date: range.from_date, to_date: range.to_date };
      if (trainerId !== 'all') params.trainer_id = trainerId;
      if (managerId !== 'all') params.manager_user_id = managerId;
      if (issueType !== 'all') params.issue_type = issueType;
      if (severity !== 'all') params.severity = severity;
      if (batchType !== 'all') params.batch_type = batchType;
      const r = await api.get('/training/audit/report', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  }, [range, trainerId, managerId, issueType, severity, batchType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const params = {};
    if (trainerId !== 'all') params.trainer_id = trainerId;
    if (managerId !== 'all') params.manager_user_id = managerId;
    api.get('/training/audit/final-batches', { params }).then((r) => setFinalData(r.data)).catch(() => {});
  }, [trainerId, managerId]);

  const k = data?.kpis || {};
  const rangeLabel = range?.label || '';

  const aoa = (rows) => [COLUMNS.map((c) => c.label), ...rows.map((r) => COLUMNS.map((c) => c.get(r)))];

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const summary = [
      ['Trainer Audit Report', rangeLabel],
      [],
      ['Total Batches', k.total_batches], ['Audits Required', k.audits_required], ['Audits Completed', k.audits_completed],
      ['Audits Pending', k.audits_pending], ['Completion %', k.completion_pct], ['Issues Found', k.issues_found],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
    const mgr = [['Training Manager', 'Assigned Batches', 'Audits Done', 'Pending', 'Issues', 'Completion %'],
      ...(data.manager_summary || []).map((m) => [m.manager_name, m.assigned_batches, m.audits_done, m.pending, m.issues, m.completion_pct])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mgr), 'Manager Performance');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa(data.rows || [])), 'Batch Details');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa(data.pending || [])), 'Pending');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa(data.exceptions || [])), 'Exceptions');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `trainer-audit-${rangeLabel}.xlsx`);
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = aoa(data.rows || []);
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `trainer-audit-${rangeLabel}.csv`);
  };

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    doc.setFillColor(124, 58, 237); doc.rect(0, 0, pw, 50, 'F');
    doc.setTextColor(255); doc.setFontSize(15); doc.setFont(undefined, 'bold');
    doc.text('Trainer Audit Report', 40, 24);
    doc.setFontSize(9); doc.setFont(undefined, 'normal');
    doc.text(`${rangeLabel}  |  Batches ${k.total_batches}  Completed ${k.audits_completed}  Pending ${k.audits_pending}  Completion ${k.completion_pct}%  Issues ${k.issues_found}`, 40, 40);
    autoTable(doc, { startY: 64, head: [['Training Manager', 'Assigned', 'Done', 'Pending', 'Issues', 'Completion %']],
      body: (data.manager_summary || []).map((m) => [m.manager_name, m.assigned_batches, m.audits_done, m.pending, m.issues, m.completion_pct]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [30, 41, 59] }, margin: { left: 40, right: 40 } });
    const cols = ['date', 'trainer_name', 'batch_number', 'batch_name', 'dpr_conducted', 'manager_name', 'audit_status', 'issue_severity', 'discrepancies'];
    const defs = COLUMNS.filter((c) => cols.includes(c.key));
    autoTable(doc, { startY: doc.lastAutoTable.finalY + 14, head: [defs.map((c) => c.label)],
      body: (data.rows || []).map((r) => defs.map((c) => c.get(r))), styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [124, 58, 237] }, margin: { left: 40, right: 40 } });
    doc.save(`trainer-audit-${rangeLabel}.pdf`);
  };

  return (
    <div className="space-y-5" data-testid="audit-report-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><BarChart3 className="w-6 h-6 text-violet-600" />Audit Report</h1>
          <p className="text-sm text-slate-500 mt-0.5">{rangeLabel} · batch-wise audit completion & exceptions</p>
        </div>
        <div className="flex items-center gap-2">
          <SyncFromCrmButton onDone={load} />
          <Button size="sm" variant="outline" onClick={exportExcel} className="h-8 text-xs" data-testid="export-excel"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
          <Button size="sm" variant="outline" onClick={exportPdf} className="h-8 text-xs" data-testid="export-pdf"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={exportCsv} className="h-8 text-xs" data-testid="export-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <PeriodSelector defaultType="day" defaultDay={yesterdayStr()} onChange={setRange} />
        <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t border-slate-100">
          <div><Label className="text-[11px] text-slate-500">Trainer</Label>
            <Select value={trainerId} onValueChange={setTrainerId}><SelectTrigger className="h-9 mt-1 w-40" data-testid="report-trainer"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All trainers</SelectItem>{(data?.trainers || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          {isAdmin && <div><Label className="text-[11px] text-slate-500">Manager</Label>
            <Select value={managerId} onValueChange={setManagerId}><SelectTrigger className="h-9 mt-1 w-40" data-testid="report-manager"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All managers</SelectItem>{(data?.managers || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-[11px] text-slate-500">Batch Type</Label>
            <Select value={batchType} onValueChange={setBatchType}><SelectTrigger className="h-9 mt-1 w-44" data-testid="report-batchtype"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All batch types</SelectItem><SelectItem value="weekday">Weekday (Mon–Thu)</SelectItem><SelectItem value="weekend">Weekend (Sat–Sun)</SelectItem></SelectContent></Select></div>
        </div>
      </Card>

      {holiday && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800 flex items-center gap-2" data-testid="holiday-banner">
          <CalendarOff className="w-4 h-4 shrink-0" />
          <span><span className="font-semibold">Public holiday: {holiday}</span> — no sessions were conducted, so there is nothing to audit on this day.</span>
        </div>
      )}

      {loading ? <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div> : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <KpiCard testid="kpi-total" label="Total Batches" value={k.total_batches ?? 0} onClick={() => setTab('batches')} active={tab === 'batches'} />
            <KpiCard testid="kpi-required" label="Audits Required" value={k.audits_required ?? 0} onClick={() => setTab('batches')} />
            <KpiCard testid="kpi-completed" label="Audits Completed" value={k.audits_completed ?? 0} color="text-emerald-600" onClick={() => setTab('completed')} active={tab === 'completed'} />
            <KpiCard testid="kpi-pending" label="Audits Pending" value={k.audits_pending ?? 0} color="text-rose-600" sub={k.na_grey ? `${k.na_grey} not-applicable` : ''} onClick={() => setTab('pending')} active={tab === 'pending'} />
            <KpiCard testid="kpi-completion" label="Completion" value={`${k.completion_pct ?? 0}%`} color="text-violet-600" />
            <KpiCard testid="kpi-issues" label="Issues Found" value={k.issues_found ?? 0} color="text-amber-600" onClick={() => setTab('exceptions')} active={tab === 'exceptions'} />
            <KpiCard testid="kpi-dpr-missing" label="DPR Missing" value={k.dpr_missing ?? 0} color="text-rose-600" sub="not filled by trainer" onClick={() => setTab('dpr-missing')} active={tab === 'dpr-missing'} />
          </div>

          {/* Manager summary */}
          <Card className="p-4" data-testid="manager-summary">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-violet-600" />Training Manager Summary <span className="text-slate-400 font-normal text-[11px]">— click a manager to see their batches</span></h3>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-1.5 pr-3">Training Manager</th><th className="py-1.5 px-3 text-right">Assigned</th><th className="py-1.5 px-3 text-right">Done</th>
                  <th className="py-1.5 px-3 text-right">Pending</th><th className="py-1.5 px-3 text-right">Issues</th><th className="py-1.5 px-3 text-right">Completion %</th><th className="py-1.5 pl-3 w-40">Progress</th>
                </tr></thead>
                <tbody>
                  {(data?.manager_summary || []).map((m) => (
                    <tr key={m.manager_user_id || m.manager_name} onClick={() => setMgrModal(m)} className="border-b border-slate-50 hover:bg-violet-50 cursor-pointer" data-testid={`mgr-row-${m.manager_user_id || 'unassigned'}`}>
                      <td className="py-2 pr-3 font-medium text-slate-700">{m.manager_name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{m.assigned_batches}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-emerald-600">{m.audits_done}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-rose-600">{m.pending}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-amber-600">{m.issues}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold">{m.completion_pct}%</td>
                      <td className="py-2 pl-3"><div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${m.completion_pct >= 90 ? 'bg-emerald-500' : m.completion_pct >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${m.completion_pct}%` }} /></div></td>
                    </tr>
                  ))}
                  {(data?.manager_summary || []).length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No data.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="report-tabs">
              <TabsTrigger value="batches" data-testid="tab-batches">Batch-wise ({data?.rows?.length || 0})</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending"><Clock className="w-3.5 h-3.5 mr-1 text-rose-500" />Pending ({data?.pending?.length || 0})</TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed"><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-500" />Completed ({(data?.rows || []).filter((r) => r.status_color === 'green' || r.status_color === 'amber').length})</TabsTrigger>
              <TabsTrigger value="exceptions" data-testid="tab-exceptions"><AlertTriangle className="w-3.5 h-3.5 mr-1 text-amber-500" />Exceptions ({data?.exceptions?.length || 0})</TabsTrigger>
              <TabsTrigger value="dpr-missing" data-testid="tab-dpr-missing"><ClipboardList className="w-3.5 h-3.5 mr-1 text-rose-500" />DPR Missing ({(data?.rows || []).filter((r) => r.dpr_status === 'missing').length})</TabsTrigger>
              <TabsTrigger value="trainer-compliance" data-testid="tab-trainer-compliance"><Award className="w-3.5 h-3.5 mr-1 text-violet-500" />Trainer Compliance ({data?.trainer_summary?.length || 0})</TabsTrigger>
              <TabsTrigger value="final" data-testid="tab-final"><Award className="w-3.5 h-3.5 mr-1 text-violet-500" />Final Audits ({finalData?.counts?.completed || 0}/{finalData?.counts?.total || 0})</TabsTrigger>
            </TabsList>
            <TabsContent value="batches" className="mt-3"><p className="text-[11px] text-slate-400 mb-1.5">Click any row for full audit details, syllabus completion & student attendance.</p><FilterTable rows={data?.rows || []} testid="batch-table" onRowClick={setDetailRow} /></TabsContent>
            <TabsContent value="pending" className="mt-3">
              <div className="flex items-center gap-2 mb-2 text-[12px] text-slate-500"><span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" /> Pending &nbsp; <span className="w-2.5 h-2.5 rounded-full bg-slate-300 inline-block" /> Not conducted (approved reason)</div>
              <FilterTable rows={data?.pending || []} testid="pending-table" onRowClick={setDetailRow} />
            </TabsContent>
            <TabsContent value="completed" className="mt-3">
              <div className="flex items-center gap-2 mb-2 text-[12px] text-slate-500"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Satisfactory &nbsp; <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" /> Completed with issue — click a row for details</div>
              <FilterTable rows={(data?.rows || []).filter((r) => r.status_color === 'green' || r.status_color === 'amber')} testid="completed-table" onRowClick={setDetailRow} />
            </TabsContent>
            <TabsContent value="exceptions" className="mt-3">
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div><Label className="text-[11px] text-slate-500">Issue Type</Label>
                  <Select value={issueType} onValueChange={setIssueType}><SelectTrigger className="h-9 mt-1 w-52" data-testid="exc-issue-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All types</SelectItem>{(data?.issue_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-[11px] text-slate-500">Severity</Label>
                  <Select value={severity} onValueChange={setSeverity}><SelectTrigger className="h-9 mt-1 w-40" data-testid="exc-severity"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem>{(data?.issue_severities || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <FilterTable rows={data?.exceptions || []} testid="exceptions-table" onRowClick={setDetailRow} />
            </TabsContent>
            <TabsContent value="dpr-missing" className="mt-3">
              <div className="flex items-center gap-2 mb-2 text-[12px] text-slate-500"><ClipboardList className="w-4 h-4 text-rose-500" />Working days where the trainer did not submit a DPR at all (holidays/leave should be excluded via the trainer master). Click a row for details.</div>
              <FilterTable rows={(data?.rows || []).filter((r) => r.dpr_status === 'missing')} testid="dpr-missing-table" onRowClick={setDetailRow} />
            </TabsContent>
            <TabsContent value="trainer-compliance" className="mt-3">
              <Card className="p-4" data-testid="trainer-summary">
                <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><Award className="w-4 h-4 text-violet-600" />Trainer Compliance <span className="text-slate-400 font-normal text-[11px]">— click a trainer to see their sessions</span></h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 pr-3">Trainer</th><th className="py-1.5 px-3">Manager</th><th className="py-1.5 px-3 text-right">Sessions</th><th className="py-1.5 px-3 text-right">Audited</th>
                      <th className="py-1.5 px-3 text-right">Pending</th><th className="py-1.5 px-3 text-right">Issues</th><th className="py-1.5 px-3 text-right">Completion %</th><th className="py-1.5 pl-3 w-40">Progress</th>
                    </tr></thead>
                    <tbody>
                      {(data?.trainer_summary || []).map((t) => (
                        <tr key={t.trainer_id || t.trainer_name} onClick={() => setTrainerModal(t)} className="border-b border-slate-50 hover:bg-violet-50 cursor-pointer" data-testid={`trainer-row-${t.trainer_id || t.trainer_name}`}>
                          <td className="py-2 pr-3 font-medium text-slate-700">{t.trainer_name}</td>
                          <td className="py-2 px-3 text-slate-500">{t.manager_name}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{t.sessions}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-emerald-600">{t.audited}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-rose-600">{t.pending}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-amber-600">{t.issues}</td>
                          <td className="py-2 px-3 text-right tabular-nums font-semibold">{t.completion_pct}%</td>
                          <td className="py-2 pl-3"><div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${t.completion_pct >= 90 ? 'bg-emerald-500' : t.completion_pct >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${t.completion_pct}%` }} /></div></td>
                        </tr>
                      ))}
                      {(data?.trainer_summary || []).length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">No data.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>
            <TabsContent value="final" className="mt-3">
              <div className="flex items-center gap-2 mb-2 text-[12px] text-slate-500">
                <Badge className="bg-slate-100 text-slate-600 border-0">Total {finalData?.counts?.total || 0}</Badge>
                <Badge className="bg-emerald-100 text-emerald-700 border-0">Done {finalData?.counts?.completed || 0}</Badge>
                <Badge className="bg-rose-100 text-rose-700 border-0">Pending {finalData?.counts?.pending || 0}</Badge>
                <span className="text-slate-400">— click a row for details</span>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-auto max-h-[65vh]" data-testid="final-audit-table">
                <table className="text-[12px] w-full">
                  <thead className="sticky top-0 bg-slate-100"><tr className="text-left text-slate-600">
                    <th className="px-2 py-1.5">Batch</th><th className="px-2 py-1.5">Batch Name</th><th className="px-2 py-1.5">Trainer</th><th className="px-2 py-1.5">Manager</th><th className="px-2 py-1.5">Ended</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Rating</th>
                  </tr></thead>
                  <tbody>
                    {(finalData?.rows || []).slice(0, 500).map((r) => (
                      <tr key={r.batch_id} onClick={() => setFinalDetail(r)} className="border-b border-slate-50 hover:bg-violet-50 cursor-pointer" data-testid={`final-row-${r.batch_id}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-700">{r.batch_number}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.batch_name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.trainer_name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.manager_name}</td>
                        <td className="px-2 py-1.5 text-slate-500">{fmtDate(r.end_date)}</td>
                        <td className="px-2 py-1.5"><span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${dot(r.status_color)}`} />{r.audit_status}</span></td>
                        <td className="px-2 py-1.5 text-slate-600">{r.overall_rating || '—'}</td>
                      </tr>
                    ))}
                    {(finalData?.rows || []).length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No completed batches.</td></tr>}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
      <RowDetailModal row={detailRow} onClose={() => setDetailRow(null)} />
      <FinalDetailModal row={finalDetail} onClose={() => setFinalDetail(null)} />
      <ManagerBatchesModal mgr={mgrModal} rows={data?.rows || []} onClose={() => setMgrModal(null)} onRowClick={(r) => { setMgrModal(null); setDetailRow(r); }} />
      {trainerModal && (
        <Dialog open={!!trainerModal} onOpenChange={(o) => !o && setTrainerModal(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="trainer-batches-modal">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-violet-600" />{trainerModal.trainer_name} — sessions</DialogTitle>
              <p className="text-[11px] text-slate-500">{trainerModal.sessions} sessions · {trainerModal.audited} audited · {trainerModal.pending} pending · {trainerModal.issues} issues · {trainerModal.completion_pct}% completion — click a row for detail</p>
            </DialogHeader>
            <div className="flex-1 overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-500"><th className="py-1.5 px-2">Date</th><th className="py-1.5 px-2">Batch</th><th className="py-1.5 px-2">Type</th><th className="py-1.5 px-2">DPR</th><th className="py-1.5 px-2">Status</th></tr></thead>
                <tbody>
                  {(data?.rows || []).filter((r) => (r.trainer_id || r.trainer_name) === (trainerModal.trainer_id || trainerModal.trainer_name)).map((r, i) => (
                    <tr key={`${r.batch_id}-${r.date}-${i}`} onClick={() => { setTrainerModal(null); setDetailRow(r); }} className="border-b border-slate-50 hover:bg-violet-50 cursor-pointer" data-testid={`trainer-batch-row-${r.batch_id}-${r.date}`}>
                      <td className="py-1.5 px-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="py-1.5 px-2"><span className="font-medium text-slate-700">{r.batch_number}</span> <span className="text-slate-400">{r.batch_name}</span></td>
                      <td className="py-1.5 px-2"><Badge className={`text-[9px] border-0 ${r.is_weekend ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>{r.is_weekend ? 'Weekend' : 'Weekday'}</Badge></td>
                      <td className="py-1.5 px-2">{r.dpr_conducted}</td>
                      <td className="py-1.5 px-2"><span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${dot(r.status_color)}`} />{r.audit_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
